/**
 * Live cart shipping estimate.
 * Reuses the same Shopify storefront APIs as shipping-calculator.js.
 * Never falls back to a hardcoded price table.
 */
if (!customElements.get('cart-shipping-estimate')) {
  customElements.define(
    'cart-shipping-estimate',
    class CartShippingEstimate extends HTMLElement {
      constructor() {
        super();
        this.valueEl = this.querySelector('[data-estimate-value]');
        this.methodEl = this.querySelector('[data-estimate-method]');
        this.zipToggleEl = this.querySelector('[data-estimate-zip-toggle]');
        this.zipFormEl = this.querySelector('[data-estimate-zip-form]');
        this.zipInputEl = this.querySelector('[data-estimate-zip-input]');
        this.zipErrorEl = this.querySelector('[data-estimate-zip-error]');
        this.storageKey = this.dataset.storageKey || 'sm_shipping_estimate_destination';
        this.retryCount = 0;
        this.maxRetries = 10;
        this.requestId = 0;
        this.debouncedRefresh = typeof debounce === 'function'
          ? debounce(this.refresh.bind(this), 400)
          : this.refresh.bind(this);
      }

      connectedCallback() {
        this.onCartChange = () => this.debouncedRefresh();
        document.addEventListener('afterCartChanged', this.onCartChange);
        document.addEventListener('afterUpdateQuantity', this.onCartChange);
        document.addEventListener('shipping-estimate:destination-updated', this.onCartChange);

        if (this.zipToggleEl) {
          this.zipToggleEl.addEventListener('click', () => this.toggleZipForm());
        }
        if (this.zipFormEl) {
          this.zipFormEl.addEventListener('submit', (event) => this.onZipSubmit(event));
        }

        this.renderDestinationControl();
        this.refresh();
      }

      disconnectedCallback() {
        document.removeEventListener('afterCartChanged', this.onCartChange);
        document.removeEventListener('afterUpdateQuantity', this.onCartChange);
        document.removeEventListener('shipping-estimate:destination-updated', this.onCartChange);
      }

      getDestination() {
        try {
          const saved = localStorage.getItem(this.storageKey);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && parsed.zip && parsed.country) {
              return {
                country: String(parsed.country).trim(),
                province: String(parsed.province || '').trim(),
                zip: String(parsed.zip).trim(),
              };
            }
          }
        } catch (e) {
          /* ignore bad storage */
        }

        const zip = (this.dataset.defaultZip || '').trim();
        const country = (this.dataset.defaultCountry || '').trim();
        if (!zip || !country) return null;

        return {
          country,
          province: (this.dataset.defaultProvince || '').trim(),
          zip,
        };
      }

      // Keeps the ZIP control in step with whatever destination we currently have.
      // Both cart views listen for the same event, so entering a ZIP in one
      // updates the label in the other.
      renderDestinationControl() {
        if (!this.zipToggleEl) return;
        const destination = this.getDestination();
        this.zipToggleEl.textContent = destination
          ? `Shipping to ${destination.zip} · Change`
          : 'Enter ZIP for a rate';
      }

      toggleZipForm() {
        if (!this.zipFormEl) return;
        const willShow = this.zipFormEl.hasAttribute('hidden');
        this.zipFormEl.toggleAttribute('hidden', !willShow);
        if (this.zipErrorEl) this.zipErrorEl.setAttribute('hidden', '');
        if (willShow && this.zipInputEl) {
          const current = this.getDestination();
          this.zipInputEl.value = current ? current.zip : '';
          this.zipInputEl.focus();
          this.zipInputEl.select();
        }
      }

      onZipSubmit(event) {
        event.preventDefault();
        if (!this.zipInputEl) return;

        const zip = this.zipInputEl.value.trim();
        const country = (this.dataset.defaultCountry || 'United States').trim();

        // US only for now, matching the countries we ship to from the cart.
        if (!/^\d{5}(-\d{4})?$/.test(zip)) {
          if (this.zipErrorEl) this.zipErrorEl.removeAttribute('hidden');
          return;
        }
        if (this.zipErrorEl) this.zipErrorEl.setAttribute('hidden', '');

        try {
          localStorage.setItem(
            this.storageKey,
            JSON.stringify({ country, province: '', zip })
          );
        } catch (e) {
          /* private mode / quota: fall through and estimate for this view only */
        }

        if (this.zipFormEl) this.zipFormEl.setAttribute('hidden', '');
        document.dispatchEvent(new CustomEvent('shipping-estimate:destination-updated'));
      }

      showLoading() {
        if (!this.valueEl) return;
        this.valueEl.innerHTML = '<span class="shipping-estimate__loading" style="color: #6B7280;">Estimating…</span>';
        this.valueEl.classList.remove('shipping-estimate__value--free', 'shipping-estimate__value--fallback');
        if (this.methodEl) this.methodEl.textContent = '';
      }

      // `quiet` is the expected path: no destination yet, so there is nothing to
      // look up. Only an actual failed lookup is worth a console warning.
      showFallback(quiet) {
        if (!this.valueEl) return;
        this.valueEl.textContent = 'Calculated at checkout';
        this.valueEl.classList.add('shipping-estimate__value--fallback');
        this.valueEl.classList.remove('shipping-estimate__value--free');
        if (this.methodEl) this.methodEl.textContent = '';
        if (!quiet) {
          console.warn('[cart-shipping-estimate] Rate lookup failed; showing "Calculated at checkout"');
        }
      }

      showSuccess(rate) {
        if (!this.valueEl) return;
        const price = Number(rate.price);
        const name = rate.presentment_name || rate.name || '';

        if (!Number.isFinite(price) || price < 0) {
          this.showFallback();
          return;
        }

        if (price === 0) {
          this.valueEl.innerHTML = '<span class="shipping-estimate__value--free">FREE</span>';
          this.valueEl.classList.add('shipping-estimate__value--free');
          this.valueEl.classList.remove('shipping-estimate__value--fallback');
        } else {
          const formatted =
            typeof Shopify !== 'undefined' && typeof Shopify.showPrice === 'function'
              ? Shopify.showPrice(Math.round(price * 100))
              : `$${price.toFixed(2)}`;
          this.valueEl.innerHTML = `from ${formatted}`;
          this.valueEl.classList.remove('shipping-estimate__value--free', 'shipping-estimate__value--fallback');
        }

        if (this.methodEl) this.methodEl.textContent = name;
      }

      serialize(obj, prefix) {
        const str = [];
        for (const p in obj) {
          if (!Object.prototype.hasOwnProperty.call(obj, p)) continue;
          const k = prefix ? `${prefix}[${p}]` : p;
          const v = obj[p];
          str.push(
            v !== null && typeof v === 'object'
              ? this.serialize(v, k)
              : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
          );
        }
        return str.join('&');
      }

      pickCheapestRate(response) {
        let rates = [];
        if (Array.isArray(response?.shipping_rates)) {
          rates = response.shipping_rates;
        } else if (response && typeof response === 'object') {
          for (const key of Object.keys(response)) {
            if (Array.isArray(response[key])) {
              rates = rates.concat(response[key].filter((item) => item && typeof item === 'object' && 'price' in item));
            }
          }
        }

        rates = rates.filter((rate) => {
          const title = `${rate.presentment_name || ''} ${rate.name || ''}`.toLowerCase();
          // Skip vault / non-standard options so we don't understate parcel shipping
          if (title.includes('vault')) return false;
          return Number.isFinite(Number(rate.price));
        });

        if (!rates.length) return null;

        return rates.reduce((best, rate) =>
          Number(rate.price) < Number(best.price) ? rate : best
        );
      }

      async refresh() {
        this.renderDestinationControl();
        const destination = this.getDestination();
        if (!destination) {
          // No ZIP from the shopper and no default configured (rates vary by
          // address, so we deliberately ship without one). Not an error.
          this.showFallback(true);
          return;
        }

        const requestId = ++this.requestId;
        this.retryCount = 0;
        this.showLoading();

        const params = { shipping_address: destination };
        const body = JSON.stringify(params);

        try {
          const prepareRes = await fetch(`${routes.cart_url}/prepare_shipping_rates.json`, {
            ...fetchConfig('json', 'POST'),
            body,
          });
          const prepareJson = await prepareRes.json();

          if (requestId !== this.requestId) return;

          if (prepareJson != null && prepareJson.ok === false) {
            this.showFallback();
            return;
          }

          await this.pollRates(params, requestId);
        } catch (e) {
          if (requestId !== this.requestId) return;
          this.showFallback();
        }
      }

      async pollRates(params, requestId) {
        while (this.retryCount < this.maxRetries) {
          if (requestId !== this.requestId) return;

          const response = await fetch(
            `${routes.cart_url}/async_shipping_rates.json?${this.serialize(params)}`,
            { ...fetchConfig('json', 'GET') }
          );
          const json = await response.json();

          if (requestId !== this.requestId) return;

          if (json != null) {
            const cheapest = this.pickCheapestRate(json);
            if (cheapest) {
              this.showSuccess(cheapest);
              return;
            }
            this.showFallback();
            return;
          }

          this.retryCount += 1;
          await new Promise((resolve) => setTimeout(resolve, 2300));
        }

        if (requestId === this.requestId) this.showFallback();
      }
    }
  );
}
